import { useEffect, type ReactNode } from "react";
import { Nav } from "@/components/Nav";
import styles from "./terms.module.css";

const PAGE_TITLE = "Terms";

/**
 * The terms for using a Company's enquiry form or phone line.
 *
 * They covered deposits, bank transfers, quote validity and equipment
 * damage, which are the rental company's terms and were the RMS's pages
 * before the fork. Intake takes an enquiry and drafts a quote into the
 * Company's own system, so that is all these cover: an enquiry is not a
 * booking, the estimate is a guide, and the hire itself is between the
 * Customer and the Company.
 *
 * Laid out like the privacy page (`privacy.tsx`), and like it, says nothing
 * about how the product is built.
 */
export default function TermsPage() {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  return (
    <div className={styles.page}>
      <Nav />

      <main className={styles.main}>
        <div className={styles.prose}>
          <h1 className={styles.title}>Terms</h1>

          <Section title="Who we are">
            <p>
              HireOS takes enquiries for the rental company you're contacting,
              through its form and its phone line. These terms cover using
              them. Anything you hire is between you and that company.
            </p>
          </Section>

          <Section title="An enquiry isn't a booking">
            <p>
              Sending an enquiry or calling doesn't book anything. The rental
              company gets back to you, and nothing is confirmed until you agree
              a quote with them.
            </p>
          </Section>

          <Section title="Prices">
            <p>
              Any price range on the form, or price given on a call, is a guide
              based on what you've told us. Your quote comes from the rental
              company and can be different.
            </p>
          </Section>

          <Section title="Your hire">
            <p>
              Payment, cancellations, changes and looking after the gear are all
              set by the rental company. Check their terms before you agree to a
              quote.
            </p>
          </Section>

          <Section title="Calls">
            <p>
              Calls are recorded and transcribed. If nobody picks up, an AI
              receptionist may answer and take down your enquiry. Anything
              talked about on a call is confirmed by the rental company, not the
              receptionist.
            </p>
          </Section>

          <Section title="Using the form">
            <p>
              Give details that are accurate, and only upload files you're
              allowed to share. Don't use the form or phone line for spam or
              anything unlawful.
            </p>
          </Section>

          <Section title="Our responsibility">
            <p>
              We work to get every enquiry through, but we can't promise the
              service is always available or free of errors. We aren't
              responsible for the rental company's quotes or hire. Nothing here
              limits your rights under the Consumer Guarantees Act or the Fair
              Trading Act.
            </p>
          </Section>

          <Section title="Privacy">
            <p>
              How we handle your information is in our{" "}
              <a href="/privacy" className={styles.link}>
                privacy policy
              </a>
              .
            </p>
          </Section>

          <Section title="Changes and questions">
            <p>
              We may update these terms, and the date below says when. Questions?
              Email{" "}
              <a href="mailto:hello@loony.io" className={styles.link}>
                hello@loony.io
              </a>
              .
            </p>
          </Section>

          <p className={styles.updated}>Last updated 14 September 2026</p>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>{title}</h2>
      {children}
    </section>
  );
}
